import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import ChatWidget from "@/components/ChatWidget";
import { authAPI } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

const HomePage = lazy(() => import("@/pages/HomePage"));
const PropertyListings = lazy(() => import("@/pages/PropertyListings"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const HomeLoanCalculator = lazy(() => import("@/pages/HomeLoanCalculator"));
const MapSearch = lazy(() => import("@/pages/MapSearch"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-terracotta border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AuthCallback({ onAuth }) {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    authAPI.googleSession(sessionId)
      .then((res) => {
        localStorage.removeItem("slabstory_token"); // Clear JWT if any, using cookie now
        onAuth(res.data.user);
        // Clean hash and redirect
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      })
      .catch((err) => {
        console.error("Google auth failed:", err);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      });
  }, [navigate, onAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta mx-auto mb-3" />
        <p className="text-sm text-slate-500">Signing you in...</p>
      </div>
    </div>
  );
}

function AppRouter({ user, setUser, authOpen, setAuthOpen }) {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously for session_id before routes render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onAuth={setUser} />;
  }

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (e) { /* ignore */ }
    localStorage.removeItem("slabstory_token");
    setUser(null);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} onAuthClick={() => setAuthOpen(true)} onLogout={handleLogout} />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/properties" element={<PropertyListings user={user} />} />
            <Route path="/property/:id" element={<PropertyDetail user={user} onAuthClick={() => setAuthOpen(true)} />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/saved" element={<Dashboard user={user} onAuthClick={() => setAuthOpen(true)} />} />
            <Route path="/admin" element={<AdminDashboard user={user} onAuthClick={() => setAuthOpen(true)} />} />
            <Route path="/home-loan-calculator" element={<HomeLoanCalculator />} />
            <Route path="/map" element={<MapSearch />} />
          </Routes>
        </Suspense>
      </main>
      <div className="mb-16 md:mb-0">
        <Footer />
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onAuth={(u) => { setUser(u); setAuthOpen(false); }} />
      <ChatWidget />
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }

    // Check existing auth (JWT token or session cookie)
    const token = localStorage.getItem("slabstory_token");
    if (token) {
      authAPI.getMe()
        .then((res) => setUser(res.data))
        .catch(() => localStorage.removeItem("slabstory_token"));
    } else {
      // Try cookie-based session
      authAPI.getMe()
        .then((res) => setUser(res.data))
        .catch(() => { /* No valid session */ });
    }
  }, []);

  return (
    <BrowserRouter>
      <AppRouter user={user} setUser={setUser} authOpen={authOpen} setAuthOpen={setAuthOpen} />
    </BrowserRouter>
  );
}

export default App;
